import React, { useEffect, useState } from "react";
import { env } from "../../../shared/config/env";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import { useAuth } from "@workos-inc/authkit-react";
import { ApiKeys, WorkOsWidgets } from "@workos-inc/widgets";
import { getWorkOsWidgetTheme } from "../../../shared/workosTheme";
import { useOrgId } from "../../../shared/auth/orgContext";
import { usePageHeader } from "../../../context/LayoutContext";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canManageWidgetApiKeys } from "../../../shared/auth/authorization";
import { useColorMode } from "../../../context/ColorModeContext";
import StationUsersPanel from "../../../shared/components/StationUsersPanel";

type ManagementTab = "classic" | "api-keys";

const WIDGETS_API_HOSTNAME = env.workos.apiHostname;

/** How long the org-id spinner may run before it's treated as stuck. */
const ORG_ID_WAIT_MS = 10_000;

const ManageUsersPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ManagementTab>("classic");
  const [orgIdTimedOut, setOrgIdTimedOut] = useState(false);

  const { mode } = useColorMode();
  const organizationId = useOrgId();
  const { getAccessToken } = useAuth();
  const permissions = usePermissions();
  const hasApiKeysPermission = canManageWidgetApiKeys(permissions);

  // organizationId normally resolves within a second; if it never does (e.g.
  // the token is missing the org claim), say so instead of spinning forever.
  useEffect(() => {
    if (organizationId) {
      setOrgIdTimedOut(false);
      return;
    }
    const timer = window.setTimeout(() => setOrgIdTimedOut(true), ORG_ID_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [organizationId]);

  usePageHeader({
    title: "Manage Users",
    subtitle: "Add, edit, and manage user accounts",
    icon: <PeopleAltIcon />,
  });

  return (
    <Container sx={{ pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid`,
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 3,
            pt: 2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, value: ManagementTab) => setActiveTab(value)}
            sx={{
              minHeight: 48,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.95rem",
                mr: 3,
                minHeight: 48,
              },
              "& .MuiTab-root:focus": { outline: "none" },
            }}
          >
            <Tab
              value="classic"
              icon={<PersonAddAlt1Icon fontSize="small" sx={{ mb: 0 }} />}
              iconPosition="start"
              label="Users"
            />
            <Tab
              value="api-keys"
              icon={<KeyOutlinedIcon fontSize="small" sx={{ mb: 0 }} />}
              iconPosition="start"
              label="API Keys"
            />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {activeTab === "classic" && (
            <Box>
              {/* organizationId is null until AuthTokenBridge decodes org_id
                  (the layout doesn't gate child routes on it); the panel's
                  queries are station-scoped, so wait for a real id rather
                  than asserting non-null. */}
              {organizationId ? (
                <StationUsersPanel stationId={organizationId} />
              ) : orgIdTimedOut ? (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  Couldn't determine which station you belong to. Refresh the
                  page; if this keeps happening, sign out and back in, or
                  contact your administrator.
                </Alert>
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              )}
            </Box>
          )}

          {activeTab === "api-keys" && (
            <Box sx={{ minHeight: 500 }}>
              {hasApiKeysPermission ? (
                // The widget's surface is full-bleed — a padded card keeps
                // its search/table/buttons off the panel borders, matching
                // the Users tab's card treatment.
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: "1px solid",
                    // Same border as the Users tab's cards (grey.400).
                    borderColor: "grey.400",
                  }}
                >
                  <WorkOsWidgets apiHostname={WIDGETS_API_HOSTNAME} theme={getWorkOsWidgetTheme(mode === "dark" ? "dark" : "light", { hasBackground: false })}>
                    <ApiKeys authToken={getAccessToken} />
                  </WorkOsWidgets>
                </Box>
              ) : (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  Missing permission <code>widgets:api-keys:manage</code>. Contact an administrator
                  to enable API key management.
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default ManageUsersPage;
